import { Link } from "react-router-dom";
import { ArrowRight, Flame, Zap, Leaf, Volume2, Wallet, Wifi, ChevronsLeftRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import Reveal from "@/components/layout/Reveal";
import SectionLabel from "@/components/layout/SectionLabel";

const BENEFITS = [
  { icon: Leaf,    title: "Up to 70% less energy",    sub: "High efficiency heat pump" },
  { icon: Wallet,  title: "Lower running costs",      sub: "Save more, every day" },
  { icon: Volume2, title: "Quieter operation",       sub: "Designed for modern living" },
  { icon: Wifi,    title: "Smarter & future ready",  sub: "Built to last, made to upgrade" },
];

const REPLACE_CARDS = [
  {
    title: "Continuous Gas Hot Water",
    desc: "Wall-mounted gas heater that fires up every time you open a hot tap — daily gas charges, combustion, ageing units.",
    badge: "Gas",
    badgeColor: "bg-orange-100 text-orange-700",
    iconBg: "bg-orange-50",
    icon: Flame,
    panelBg: "bg-[#F4F4F0]",
    img: "/replace/gas-continuous.png",
    alt: "Wall-mounted continuous gas hot water unit on a domestic weatherboard wall",
    fit: "cover",
  },
  {
    title: "Gas Storage Hot Water",
    desc: "Floor-standing gas cylinder with a burner — keeps water hot 24/7, even when no-one's home, burning gas constantly.",
    badge: "Gas",
    badgeColor: "bg-orange-100 text-orange-700",
    iconBg: "bg-orange-50",
    icon: Flame,
    panelBg: "bg-[#F4F4F0]",
    img: "/replace/gas-storage.png",
    alt: "Floor-standing gas storage hot water cylinder in a clean home utility corner",
    fit: "cover",
  },
  {
    title: "Electric Storage Cylinders",
    desc: "The classic Kiwi cylinder — a resistance element keeps a tank of water hot, drawing power day and night.",
    badge: "Electric",
    badgeColor: "bg-blue-100 text-blue-700",
    iconBg: "bg-blue-50",
    icon: Zap,
    panelBg: "bg-[#EEF1F5]",
    img: "/replace/electric-storage.png",
    alt: "Domestic electric storage hot water cylinder in a residential garage",
    fit: "cover",
  },
  {
    title: "Older Inefficient Systems",
    desc: "Ageing tanks with leaks, corrosion, poor insulation and rising running costs — time for a smarter upgrade.",
    badge: "Legacy",
    badgeColor: "bg-stone-100 text-stone-700",
    iconBg: "bg-stone-50",
    icon: Flame,
    panelBg: "bg-[#EFEDE7]",
    img: "/replace/legacy.png",
    alt: "Older worn cream hot water cylinder in a basement utility space",
    fit: "cover",
  },
];

export const WhatWeReplaceSection = () => {
  return (
    <section data-testid="what-we-replace" className="py-20 sm:py-28 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        {/* Pill */}
        <Reveal>
          <SectionLabel>What we replace</SectionLabel>
        </Reveal>

        {/* TOP: 2-column hero — copy + benefits on left, before/after on right */}
        <div className="mt-6 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* LEFT */}
          <Reveal className="lg:col-span-6">
            <h2 className="font-heading text-3xl sm:text-5xl font-bold text-[#0B132B] leading-[1.05]">
              From old &amp; inefficient to <span className="text-[#10B981]">smart &amp; electric.</span>
            </h2>
            <p className="mt-5 text-lg text-[#475569] leading-relaxed max-w-xl">
              AeroMax Energy helps Kiwi households transition away from outdated gas and electric hot water systems — and into the modern, efficient NeoPower Black Shield.
            </p>

            <ul className="mt-9 space-y-5">
              {BENEFITS.map((b, i) => (
                <li
                  key={i}
                  data-testid={`replace-benefit-${i}`}
                  className="flex items-center gap-4"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#10B981]/12 ring-1 ring-[#10B981]/20 text-[#10B981] shrink-0">
                    <b.icon className="h-5 w-5" strokeWidth={2.2} />
                  </span>
                  <div>
                    <p className="font-heading font-bold text-[#0B132B] leading-tight">{b.title}</p>
                    <p className="text-sm text-[#475569] mt-0.5 leading-snug">{b.sub}</p>
                  </div>
                </li>
              ))}
            </ul>
          </Reveal>

          {/* RIGHT: premium split before / after */}
          <Reveal delay={120} className="lg:col-span-6">
            <div
              data-testid="replace-before-after"
              className="relative rounded-[1.75rem] overflow-hidden ring-1 ring-[#E2E8F0] shadow-[0_30px_80px_-25px_rgba(11,19,43,0.28)] bg-[#0B132B]"
            >
              <div className="grid grid-cols-2 h-[460px] sm:h-[560px]">
                {/* OLD half */}
                <div className="relative overflow-hidden bg-gradient-to-b from-[#F4F4F0] to-[#E4E2DA]">
                  <img
                    src="/replace/legacy.png"
                    alt="Old hot water system being replaced"
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#0B132B]/10 pointer-events-none" />
                  <span className="absolute top-4 left-4 z-[2] inline-flex items-center px-3 py-1.5 rounded-full bg-white shadow-md text-[10px] tracking-[0.18em] uppercase font-bold text-[#0B132B]">
                    Old System
                  </span>
                </div>

                {/* NEW half */}
                <div className="relative overflow-hidden bg-[#0B132B]">
                  <img
                    src="/products/neopower_innovation.png"
                    alt="New NeoPower Black Shield heat pump"
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                  />
                  <span className="absolute top-4 right-4 z-[2] inline-flex items-center px-3 py-1.5 rounded-full bg-white shadow-md text-[10px] tracking-[0.18em] uppercase font-bold text-[#0B132B]">
                    New NeoPower Black Shield
                  </span>
                </div>
              </div>

              {/* Center divider + handle */}
              <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-white/80 shadow-[0_0_24px_rgba(255,255,255,0.5)] pointer-events-none">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-14 w-14 rounded-full bg-white shadow-[0_10px_30px_-5px_rgba(0,0,0,0.35)] ring-1 ring-[#E2E8F0] flex items-center justify-center text-[#0B132B]">
                  <ChevronsLeftRight className="h-5 w-5" strokeWidth={2.2} />
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* BELOW: subheading + 4 cards */}
        <Reveal className="mt-20 sm:mt-24">
          <h3
            data-testid="replace-cards-heading"
            className="font-heading text-2xl sm:text-3xl font-bold text-[#0B132B] leading-tight"
          >
            Every old hot water setup, handled.
          </h3>
          <p className="mt-2 text-[#475569] max-w-2xl">
            Whatever you&apos;re running today, we can swap it out for the NeoPower Black Shield.
          </p>
        </Reveal>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
          {REPLACE_CARDS.map((c, i) => (
            <Reveal key={i} delay={i * 70}>
              <Card
                data-testid={`replace-card-${i}`}
                className="group relative h-full flex flex-col rounded-2xl border-[#E2E8F0] bg-white overflow-hidden shadow-sm hover:shadow-xl hover:shadow-emerald-500/8 hover:-translate-y-1 hover:border-[#10B981]/30 transition-all duration-300"
              >
                {/* Photo panel */}
                <div className={`relative aspect-[3/4] overflow-hidden ${c.panelBg}`}>
                  {/* Category badge top-left */}
                  <span
                    className={`absolute top-4 left-4 z-10 inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase ${c.badgeColor}`}
                  >
                    {c.badge}
                  </span>
                  {/* Replace chip top-right */}
                  <span className="absolute top-4 right-4 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white shadow-sm text-[10px] font-semibold text-[#475569]">
                    <c.icon className="h-3 w-3" /> Replace
                  </span>

                  <img
                    src={c.img}
                    alt={c.alt}
                    loading="lazy"
                    className={`absolute inset-0 w-full h-full ${
                      c.fit === "cover" ? "object-cover" : "object-contain p-6"
                    } group-hover:scale-[1.04] transition-transform duration-500`}
                  />
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-1">
                  <p className="font-heading text-lg font-bold text-[#0B132B] leading-snug">
                    {c.title}
                  </p>
                  <p className="text-sm text-[#475569] mt-2 leading-relaxed flex-1">
                    {c.desc}
                  </p>

                  <div className="mt-5 pt-4 border-t border-[#E2E8F0]">
                    <Link
                      to="/neopower-black-shield"
                      data-testid={`replace-card-cta-${i}`}
                      className="inline-flex items-center gap-2 text-[12px] font-bold tracking-[0.18em] uppercase text-[#10B981] hover:text-[#059669] transition-colors"
                    >
                      Upgrade to NeoPower
                      <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" strokeWidth={2.4} />
                    </Link>
                  </div>
                </div>
              </Card>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
